<glxy-page>
  <glxy-page-toolbar>
    <glxy-page-nav>
      <glxy-page-nav-button useRelativePath="true" previousPageUrl="../../"></glxy-page-nav-button>
    </glxy-page-nav>
    @if (pageName$ | async; as pageName) {
      <glxy-page-title>
        <span>{{ pageName }} - {{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.TEST_RUN_SUFFIX' | translate }}</span>
        @if (businessName()) {
          <span class="business-name">({{ businessName() }})</span>
        }
      </glxy-page-title>
      <glxy-page-actions>
        @if (testRun$ | async; as testRun) {
          @if (testRun.buildUrl) {
            <a class="cloud-build-link" [href]="testRun.buildUrl" target="_blank">
              <button mat-flat-button color="primary">
                {{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.VIEW_WORKFLOW' | translate }}
              </button>
            </a>
          }
        }
      </glxy-page-actions>
    }
  </glxy-page-toolbar>
  <glxy-page-wrapper widthPreset="wide">
    <mat-card>
      <mat-card-header>
        @if (testRun$ | async; as testRun) {
          <mat-card-title>
            <div class="title">
              <span class="run-name">{{ getRunName(testRun) }}</span>
              <span class="run-meta"
                >{{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.TEST_RESULTS' | translate }}
                {{ testRun.created | date: 'medium' }}</span
              >
              @if (statusMap.get(testRun.status); as status) {
                <div class="status" [ngClass]="status.class">
                  <mat-icon>{{ status.icon }}</mat-icon
                  >{{ status.name | translate }}
                </div>
              }
              <div>{{ getPassedCount(testRun) }} {{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.TESTS_PASSED' | translate }}</div>
              @if (getAssistantVersion(testRun); as assistantVersion) {
                <span class="run-meta">{{ assistantVersion }}</span>
              }
              @if (testRun.status === 'in_progress') {
                <button mat-stroked-button (click)="cancelTestRun()" class="refresh-button">
                  {{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.CANCEL_RUN' | translate }}
                </button>
                <button
                  mat-stroked-button
                  (click)="retryStuckTestResults()"
                  [glxyTooltip]="'AI_ASSISTANT.TESTING.RUN_DISPLAY.RETRY_TOOLTIP' | translate"
                >
                  {{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.RETRY_STUCK' | translate }}
                </button>
              }
            </div>
          </mat-card-title>
        }
      </mat-card-header>
      <mat-card-content>
        @if (testRun$ | async; as testRun) {
          @if (testRun.isSuccessEvaluation) {
            <section class="success-evaluation">
              <h3>{{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.SUCCESS_EVALUATION_BADGE' | translate }}</h3>
              @if (testRun.sourceCallRecordId) {
                <div class="source-row">
                  <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.SOURCE_CALL_RECORD_LABEL' | translate }}</span>
                  @if (sourceRecordingUrl(testResultsSig())) {
                    <audio controls preload="metadata" [src]="sourceRecordingUrl(testResultsSig())"></audio>
                  } @else {
                    <span class="id">{{ testRun.sourceCallRecordId }}</span>
                  }
                </div>
              }
              @if (testRun.sourceConversationId) {
                <div class="source-row">
                  <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.SOURCE_CONVERSATION_LABEL' | translate }}</span>
                  <span class="id">{{ testRun.sourceConversationId }}</span>
                </div>
              }
            </section>
            <mat-divider></mat-divider>
          }
        }
        <glxy-table-container
          class="simple-table"
          [dataSource]="dataSource"
          [columns]="columns()"
          [pageSizeOptions]="[10, 20, 30]"
          [pageSize]="10"
          [border]="false"
          [fullWidth]="false"
        >
          <glxy-table-content-header
            showFilters="false"
            showFiltersButton="false"
            showSort="false"
          ></glxy-table-content-header>
          <table mat-table matSort (matSortChange)="sortData($event)">
            <tr mat-header-row *matHeaderRowDef="[]"></tr>

            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>
                {{ 'AI_ASSISTANT.TESTING.TEST_CASES.NAME' | translate }}
              </th>
              <td mat-cell *matCellDef="let element">
                <a (click)="openDialog(element)">{{ element.name }}</a>
              </td>
            </ng-container>

            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef>{{ 'AI_ASSISTANT.TESTING.RUN_TABLE.STATUS' | translate }}</th>
              <td mat-cell *matCellDef="let element">
                @if (statusMap.get(element.status); as status) {
                  <div class="status" [ngClass]="status.class">
                    <mat-icon>{{ status.icon }}</mat-icon
                    >{{ status.name | translate }}
                  </div>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="score">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>
                {{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.SCORE' | translate }}
              </th>
              <td mat-cell *matCellDef="let element">
                {{ element.score || 0 | percent }}
              </td>
            </ng-container>

            <ng-container matColumnDef="model">
              <th mat-header-cell *matHeaderCellDef>{{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.MODEL' | translate }}</th>
              <td mat-cell *matCellDef="let element">
                {{ getModelAndReasoning(element) }}
              </td>
            </ng-container>

            <ng-container matColumnDef="latency">
              <th
                mat-header-cell
                *matHeaderCellDef
                [glxyTooltip]="'AI_ASSISTANT.TESTING.RUN_DISPLAY.LATENCY_TOOLTIP' | translate"
              >
                {{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.LATENCY' | translate }}
                <mat-icon inline="true">info</mat-icon>
              </th>
              <td mat-cell *matCellDef="let element">{{ element.latency || 0 | number }}s</td>
            </ng-container>

            <ng-container matColumnDef="agentArchitecture">
              <th mat-header-cell *matHeaderCellDef>
                {{ 'AI_ASSISTANT.TESTING.TEST_CASES.AGENT_ARCHITECTURE' | translate }}
              </th>
              <td mat-cell *matCellDef="let element">
                @if (element.agentArchitecture) {
                  {{ getAgentArchitectureName(element.agentArchitecture) }}
                } @else {
                  <span class="text-muted">{{ 'AI_ASSISTANT.TESTING.ARCHITECTURE.UNSET' | translate }}</span>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="duration">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>
                {{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.CALL_DURATION' | translate }}
              </th>
              <td mat-cell *matCellDef="let element">{{ getFormattedCallDuration(element.callDuration) }}</td>
            </ng-container>

            <tr mat-row *matRowDef="let row; columns: []"></tr>

            <ng-container *matNoDataRow="let row">
              <td [attr.colspan]="columns().length">
                @if (testRunSig()?.isSuccessEvaluation) {
                  <glxy-empty-state size="small">
                    <glxy-empty-state-title>
                      {{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.NO_MATCHING_CASES_TITLE' | translate }}
                    </glxy-empty-state-title>
                    <p>{{ 'AI_ASSISTANT.TESTING.RUN_DISPLAY.NO_MATCHING_CASES' | translate }}</p>
                  </glxy-empty-state>
                }
              </td>
            </ng-container>
          </table>
        </glxy-table-container>
      </mat-card-content>
    </mat-card>
  </glxy-page-wrapper>
</glxy-page>
